import * as Sentry from '@sentry/react';
import { clearSessionCaches } from 'core/utils/clearSessionCaches';
import { getByodAppSession, isSessionExpired } from 'core/utils/byodAppSession';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { Component, ErrorInfo, ReactNode } from 'react';

interface AppErrorBoundaryProps {
	children: ReactNode;
}

interface AppErrorBoundaryState {
	hasError: boolean;
}

const hasValidSession = (): boolean =>
	Boolean(getByodAppSession()?.token_key) && !isSessionExpired();

/**
 * Top-level render error guard. Without it any render-time throw unmounts the
 * whole tree and leaves a blank white screen. If the crash happened because the
 * session ended (e.g. 401 wiped storage mid-render), send the user to login;
 * otherwise show a reloadable fallback.
 */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
	state: AppErrorBoundaryState = { hasError: false };

	static getDerivedStateFromError(): AppErrorBoundaryState {
		return { hasError: true };
	}

	componentDidCatch(error: Error, info: ErrorInfo): void {
		if (!hasValidSession()) {
			clearSessionCaches();
			if (window.location.pathname !== ROUTES.LOGIN) {
				window.location.replace(ROUTES.LOGIN);
			}
			return;
		}
		Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
	}

	render(): ReactNode {
		if (!this.state.hasError) {
			return this.props.children;
		}

		return (
			<div
				role="alert"
				style={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					gap: 16,
					minHeight: '100vh',
					padding: 16,
					textAlign: 'center',
				}}
			>
				<h2 style={{ margin: 0 }}>Something went wrong</h2>
				<p style={{ margin: 0 }}>Please reload the page to continue.</p>
				<button type="button" onClick={() => window.location.reload()}>
					Reload
				</button>
			</div>
		);
	}
}

export default AppErrorBoundary;
